import { Plan, User } from '@/api/models';
import { Button } from '@/components/ui/Button';
import { Pagination } from '@/components/ui/Pagination';
import { getAvatarColor } from '@/lib/colors';
import { formatDateWithYear } from '@/lib/dateUtils';
import { useNotifications } from '@/lib/notifications';
import { useNotificationMutations } from '@/pages/notifications/hooks/useNotifications';
import { UserPlanMutationsActions } from '@/pages/users/hooks/useUserPlanMutations';
import {
  PlanFilter,
  UserPlansActions,
  UserPlansState,
} from '@/pages/users/hooks/useUserPlans';
import { AdminSendNotificationDTOType } from '@coffice/shared/api';
import { mdiBell, mdiDelete, mdiPlus } from '@mdi/js';
import { DateTime } from 'luxon';
import * as React from 'react';

type PlanDisplayState = 'upcoming' | 'now' | 'past';

const derivePlanState = (plan: Plan): PlanDisplayState => {
  const now = new Date();
  const startAt = new Date(plan.startAt);
  const endAt = new Date(plan.endAt);

  if (endAt < now) return 'past';
  if (startAt <= now && endAt >= now) return 'now';
  return 'upcoming';
};

interface UserPlansTabProps {
  user: User;
  plansState: UserPlansState;
  plansActions: UserPlansActions;
  mutationActions: UserPlanMutationsActions;
  isDeleting: boolean;
  onCreatePlan: () => void;
}

export const UserPlansTab: React.FC<UserPlansTabProps> = ({
  user,
  plansState,
  plansActions,
  mutationActions,
  isDeleting,
  onCreatePlan,
}) => {
  const { plans, total, totalPages, isLoading, filters } = plansState;
  const { sendNotification, isSending } = useNotificationMutations();
  const { addNotification } = useNotifications();

  const handleSendPlanReminder = async (plan: Plan) => {
    if (!user.id || !plan.id) return;

    const businessName = plan.business?.name || 'your space';
    const startDateTime = DateTime.fromISO(plan.startAt!);
    const startTime = startDateTime.toFormat('HH:mm');
    const startDate = startDateTime.toFormat('MMM d');

    try {
      await sendNotification({
        userId: user.id,
        title: 'Plan Reminder',
        body: `Don't forget your plan at ${businessName} on ${startDate} at ${startTime}`,
        type: AdminSendNotificationDTOType.plan_reminder,
        data: {
          planId: plan.id,
          businessId: plan.business?.id,
          businessName,
        },
      });
      addNotification({
        type: 'success',
        title: 'Success',
        message: 'Reminder sent successfully',
      });
    } catch {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to send reminder',
      });
    }
  };

  const getStateBadge = (state: PlanDisplayState) => {
    switch (state) {
      case 'now':
        return (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-coffice-blue/20 text-coffice-blue'>
            Happening now
          </span>
        );
      case 'past':
        return (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-grey-100 text-grey-600'>
            Past
          </span>
        );
      case 'upcoming':
      default:
        return (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-coffice-yellow/30 text-grey-900'>
            Upcoming
          </span>
        );
    }
  };

  const getPlanCardStyle = (state: PlanDisplayState) => {
    switch (state) {
      case 'now':
        return 'border-coffice-blue/30 bg-coffice-blue/5';
      case 'past':
        return 'border-grey-200 bg-grey-50/50 opacity-60';
      case 'upcoming':
      default:
        return 'border-grey-200 bg-white';
    }
  };

  if (isLoading) {
    return (
      <div className='p-6'>
        <div className='animate-pulse space-y-4'>
          <div className='h-10 bg-grey-200 rounded w-32'></div>
          <div className='h-8 bg-grey-200 rounded'></div>
          <div className='h-16 bg-grey-200 rounded'></div>
          <div className='h-16 bg-grey-200 rounded'></div>
          <div className='h-16 bg-grey-200 rounded'></div>
        </div>
      </div>
    );
  }

  return (
    <div className='p-6 space-y-4'>
      {/* Header with Create Button */}
      <div className='flex items-center justify-between'>
        <div className='text-sm text-grey-600'>
          {total} plan{total !== 1 ? 's' : ''} found
        </div>
        <Button
          icon={mdiPlus}
          label='Create Plan'
          color='primary'
          small
          onClick={onCreatePlan}
        />
      </div>

      {/* Filters */}
      <div className='flex items-center space-x-4'>
        <select
          value={filters.filter ?? ''}
          onChange={e =>
            plansActions.setFilter(
              e.target.value === ''
                ? undefined
                : (e.target
                    .value as (typeof PlanFilter)[keyof typeof PlanFilter]),
            )
          }
          className='text-sm border-grey-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500'
        >
          <option value=''>All Plans</option>
          <option value={PlanFilter.upcoming}>Upcoming</option>
          <option value={PlanFilter.history}>History</option>
        </select>

        <select
          value={filters.sortBy}
          onChange={e =>
            plansActions.setSortBy(
              e.target.value as 'startAt' | 'endAt' | 'createdAt',
            )
          }
          className='text-sm border-grey-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500'
        >
          <option value='startAt'>Sort by Start Date</option>
          <option value='endAt'>Sort by End Date</option>
          <option value='createdAt'>Sort by Created</option>
        </select>

        <select
          value={filters.sortOrder}
          onChange={e =>
            plansActions.setSortOrder(e.target.value as 'asc' | 'desc')
          }
          className='text-sm border-grey-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500'
        >
          <option value='desc'>Newest First</option>
          <option value='asc'>Oldest First</option>
        </select>
      </div>

      {/* Plans List */}
      {plans.length === 0 ? (
        <div className='text-center py-12'>
          <p className='text-grey-500'>No plans found for this user.</p>
          <Button
            icon={mdiPlus}
            label='Create First Plan'
            color='primary'
            className='mt-4'
            onClick={onCreatePlan}
          />
        </div>
      ) : (
        <div className='space-y-3'>
          {plans.map(plan => {
            const state = derivePlanState(plan);
            return (
              <div
                key={plan.id}
                className={`p-4 rounded-lg border transition-shadow hover:shadow-md ${getPlanCardStyle(state)}`}
              >
                <div className='flex items-start justify-between'>
                  <div className='flex-1'>
                    {/* Business Info */}
                    <div className='flex items-center space-x-3 mb-2'>
                      {(() => {
                        const avatarColor = getAvatarColor(
                          plan.business?.id || 0,
                        );
                        return (
                          <div
                            className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-semibold ${avatarColor.bg} ${avatarColor.text}`}
                          >
                            {plan.business?.name?.[0]?.toUpperCase() || 'B'}
                          </div>
                        );
                      })()}
                      <div>
                        <p className='font-medium text-grey-900'>
                          {plan.business?.name || 'Unknown Business'}
                        </p>
                        <p className='text-xs text-grey-500'>
                          {plan.business?.address}, {plan.business?.city}
                        </p>
                      </div>
                    </div>

                    {/* Plan Details */}
                    <div className='grid grid-cols-2 gap-4 mt-3'>
                      <div>
                        <dt className='text-xs text-grey-500'>Start</dt>
                        <dd className='text-sm text-grey-900'>
                          {formatDateWithYear(plan.startAt)}
                        </dd>
                      </div>
                      <div>
                        <dt className='text-xs text-grey-500'>End</dt>
                        <dd className='text-sm text-grey-900'>
                          {formatDateWithYear(plan.endAt)}
                        </dd>
                      </div>
                    </div>
                  </div>

                  {/* State and Actions */}
                  <div className='ml-4 flex flex-col items-end space-y-2'>
                    {getStateBadge(state)}

                    <div className='flex items-center space-x-1 mt-2'>
                      {(state === 'upcoming' || state === 'now') && (
                        <Button
                          icon={mdiBell}
                          color='warning'
                          small
                          outline
                          disabled={isSending}
                          onClick={() => handleSendPlanReminder(plan)}
                          title='Send reminder'
                        />
                      )}
                      <Button
                        icon={mdiDelete}
                        color='danger'
                        small
                        disabled={isDeleting}
                        onClick={() =>
                          mutationActions.handleDeletePlan(user.id!, plan)
                        }
                        title='Delete plan'
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={filters.page}
          totalPages={totalPages}
          onPageChange={plansActions.setPage}
          totalItems={total}
          itemsPerPage={filters.limit}
          className='mt-4'
        />
      )}
    </div>
  );
};
